import type { FC } from 'react';
import React, { memo, useCallback, useContext, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import type { EditorContentMode } from '@atlaskit/editor-common/types/editor-appearance';
import ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import { getPageElementCounts } from '@atlaskit/editor-common/utils/page-element-counts';
import type { ADNode } from '@atlaskit/editor-common/utils/validator';

import { useExternalLinkHandlers } from '@confluence/adf-renderer/entry-points/useExternalLinkHandlers.ts';
import { useRendererAnnotationProviderOptions } from '@confluence/annotation-provider/entry-points/useRendererAnnotationProviderOptions.ts';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import {
	NBMRendererContent,
	resolveNBMRenderConfig,
} from '@confluence/content-renderer-legacy-macros/entry-points/NBMRenderer.ts';
import { useEditorContentModeValue } from '@confluence/editor-content-mode-button/entry-points/useEditorContentModeValue.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';

import type { LegacyContentMacroParams } from './LegacyContentMacroParams.ts';

export interface NBMRendererClientProps {
	contentMode?: EditorContentMode;
	node: ExtensionParams<LegacyContentMacroParams>;
}

export const NBMRendererClient: FC<NBMRendererClientProps> = memo(({ contentMode, node }) => {
	const spaceId = useSpaceId();
	const spaceKey = useSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType = 'page'] = useContentType();
	const { environment, cloudId, userId, activationId } = useSessionData();
	const { getExtensionHandlers, populateExtensionProviders } = useContext(RendererExtensionContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { mode: editorContentMode } = useEditorContentModeValue({ contentId });
	const effectiveContentMode = contentMode ?? editorContentMode;

	const { adfDocument, appearance } = resolveNBMRenderConfig(node);

	/**
	 * Both context functions use classic renderer types that are currently exposed as `any`.
	 */
	const extensionHandlers = useMemo(
		() =>
			getExtensionHandlers?.(
				{
					adf: adfDocument as ADNode,
					contentId,
					spaceKey,
					contentType,
					appearance: 'full-width',
					referentialityDataSourceProvider: {},
				},
				createAnalyticsEvent,
			),
		[adfDocument, contentId, contentType, createAnalyticsEvent, getExtensionHandlers, spaceKey],
	);

	const annotationProvider = useRendererAnnotationProviderOptions({
		pageId: contentId ?? '',
		validAnnotationsCallback: () => {},
		triggerAnalyticsEvent: createAnalyticsEvent,
		updateDocument: () => {},
		isArchived: false,
	});

	const getDataProviders = useCallback(() => {
		const providerFactory = ProviderFactory.create({});

		if (populateExtensionProviders) {
			populateExtensionProviders(providerFactory, {
				activationId,
				accountId: userId,
				cloudId,
				containerId: spaceKey,
				contentId,
				contentType,
				createAnalyticsEvent,
				environment,
				extensionHandlers,
				intl,
				spaceKey,
				spaceId,
			});
		}

		return providerFactory;
	}, [
		populateExtensionProviders,
		activationId,
		userId,
		cloudId,
		spaceKey,
		contentId,
		contentType,
		createAnalyticsEvent,
		environment,
		extensionHandlers,
		intl,
		spaceId,
	]);

	const { eventHandlers, onSetLinkTarget } = useExternalLinkHandlers();

	useEffect(() => {
		if (contentId && createAnalyticsEvent && adfDocument) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'rendererInitialised',
					actionSubject: 'legacyContentMacro',
					source: 'legacyContentMacro',
					attributes: {
						objectId: contentId,
						pageElementCounts: getPageElementCounts(adfDocument),
					},
				},
			}).fire();
		}
	}, [adfDocument, contentId, createAnalyticsEvent, node.localId]);

	return (
		<NBMRendererContent
			allowAnnotations
			annotationProvider={annotationProvider}
			appearance={appearance}
			contentId={contentId}
			contentMode={effectiveContentMode}
			dataProviders={getDataProviders()}
			adfDocument={adfDocument}
			eventHandlers={eventHandlers}
			onSetLinkTarget={onSetLinkTarget}
		/>
	);
});
